import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { hardSignout, signout } from 'slices/auth';
import { selectChannels } from 'slices/channels';
import { isSameObject } from 'utils/comparators';
import { getRandomUuid } from 'utils/uuid';

const initialState = {
  collection: null,
  drafts: {},
  status: null,
};

// Cadence of the chats/chat refresh polls (Chats list and an open chat).
export const CHATS_POLL_INTERVAL = 5000;

export const refreshChat = createAsyncThunk('chat/pull', async ([chatsAPI, id], { getState }) => {
  const state = getState();
  // `channels` is null until the chats list / channel search has loaded it. On a session
  // restored straight onto /chats/:id (deep link after login) neither runs, so guard the lookup
  // — an unguarded `null.find` threw here, rejecting the thunk so the chat never reached the
  // store and the page sat on its loading skeleton forever. With no channel list yet we fall
  // back to the authenticated `get` (works for any chat type; SessionBootstrap then loads the
  // list so the next poll can use the precached endpoint for public channels).
  // Only an unsubscribed public channel reads the precached static snapshot. Once subscribed,
  // the authenticated `get` is used so the response carries the user's real membership and
  // `subscribed: true` (the precached snapshot is always `subscribed: false`).
  const chat = selectChats(state)?.find((c) => c.id === id);
  const isUnsubscribedChannel =
    !chat?.subscribed && !!selectChannels(state)?.find((c) => c.id === id);
  const methodName = isUnsubscribedChannel ? 'getPrecached' : 'get';
  const data = await chatsAPI[methodName](id);

  return data;
});

export const refreshChats = createAsyncThunk('chats/pull', async (chatsAPI) => {
  const data = await chatsAPI.all();

  return data;
});

export const chatsSlice = createSlice({
  name: 'chats',
  initialState,
  reducers: {
    // Pure reducer: appends an optimistic outgoing message. The API call lives in the sendMessage thunk.
    messageQueued: (state, action) => {
      const { collection: chats } = state;
      const { chatId, sendingMessage } = action.payload;

      if (!chats) return;

      const chat = chats.find((c) => c.id === chatId);
      if (!chat) return;

      chat.sendingMessages ||= [];
      chat.sendingMessages.push(sendingMessage);
      chat.last_read_message_id = sendingMessage.id;
      chat.last_viewed_message_id = sendingMessage.id;
    },
    // Pure reducer: applies chat name/photo changes. The API call lives in the updateChat thunk.
    chatUpdated: (state, action) => {
      const { collection: chats } = state;
      const { id, name, photoUrl } = action.payload;

      if (!chats) return;

      const chat = chats.find((c) => c.id === id);
      if (!chat) return;

      chat.name = name;
      chat.photoUrl = photoUrl;
    },
    setDraft: (state, action) => {
      const { chatId, text } = action.payload;
      if (text) {
        state.drafts[chatId] = text;
      } else {
        delete state.drafts[chatId];
      }
    },
    // Pure reducer: replaces a chat membership. Diffing + API call live in the updateMembership thunk.
    membershipUpdated: (state, action) => {
      const { collection: chats } = state;
      const { chatId, membership } = action.payload;

      if (!chats) return;

      const chat = chats.find((c) => c.id === chatId);
      if (!chat) return;

      chat.membership = membership;
    },
    // Pure reducer: flips channel subscription state. The API call lives in the subscribe/unsubscribe thunks.
    subscriptionChanged: (state, action) => {
      const { collection: chats } = state;
      const { chatId, subscribed } = action.payload;

      if (!chats) return;

      const chat = chats.find((c) => c.id === chatId);
      if (!chat) return;

      chat.subscribed = subscribed;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signout.pending, (state) => {
        state.collection = null;
        state.drafts = {};
        state.status = null;
      })
      .addCase(hardSignout.pending, (state) => {
        state.collection = null;
        state.drafts = {};
        state.status = null;
      })
      .addCase(refreshChats.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(refreshChats.fulfilled, (state, action) => {
        state.collection = action.payload;
      })
      .addCase(refreshChat.fulfilled, (state, action) => {
        state.collection ||= [];
        const { collection: chats } = state;
        const { payload: chat } = action;
        const chatIndex = chats.findIndex((c) => c.id === chat.id);
        if (chatIndex === -1) {
          chats.push(chat);
        } else {
          chats[chatIndex] = {
            ...chat,
            sendingMessages: chats[chatIndex].sendingMessages?.filter(
              (m) => !chat.last_messages.map(({ id }) => id).includes(m.id)
            ),
          };
        }
      });
  },
});

export const { chatUpdated, membershipUpdated, messageQueued, setDraft, subscriptionChanged } =
  chatsSlice.actions;

export const selectChats = (state) => state.chats.collection;
export const selectDraft = (chatId) => (state) => state.chats.drafts[chatId] || '';

// Helper function to calculate unread count for a single chat
export const getUnreadCount = (chat) => {
  if (!chat?.last_messages) return 0;

  const indexOfLastReadMessage = chat.last_messages
    .map(({ id }) => id)
    .indexOf(chat.last_read_message_id);
  const lastMessagesCount = chat.last_messages.length;

  if (lastMessagesCount === 0) {
    return 0;
  } else if (indexOfLastReadMessage === -1 && chat.last_read_message_id != null) {
    return Infinity;
  } else {
    return lastMessagesCount - (indexOfLastReadMessage + 1);
  }
};

// Selector to get unread count for a specific chat
export const selectChatUnreadCount = (chatId) => (state) => {
  const chats = selectChats(state);
  if (!chats) return 0;
  const chat = chats.find((c) => c.id === chatId);
  return getUnreadCount(chat);
};

// Selector to get total unread count across all chats
export const selectTotalUnreadCount = (state) => {
  const chats = selectChats(state);
  if (!chats) return 0;

  return chats.reduce((total, chat) => {
    const unreadCount = getUnreadCount(chat);
    if (unreadCount === Infinity) {
      return Infinity;
    }
    return total === Infinity ? Infinity : total + unreadCount;
  }, 0);
};

// Thunks keep side effects (API calls) out of reducers. The call signature is unchanged —
// e.g. dispatch(sendMessage([MessagesAPI, { chatId, text }])) — so existing call-sites keep working.
export const sendMessage =
  ([MessagesAPI, { chatId, text }]) =>
  (dispatch, getState) => {
    const chat = selectChats(getState())?.find((c) => c.id === chatId);
    if (!chat) return;

    const sendingMessage = { id: getRandomUuid(), text, membership: chat.membership };
    MessagesAPI.create({ chatId, ...sendingMessage });
    dispatch(messageQueued({ chatId, sendingMessage }));
  };

export const updateChat =
  ([ChatsAPI, { id, name, photoUrl }]) =>
  (dispatch, getState) => {
    const chat = selectChats(getState())?.find((c) => c.id === id);
    if (!chat) return;

    ChatsAPI.update({ id, name, photoUrl });
    dispatch(chatUpdated({ id, name, photoUrl }));
  };

export const updateMembership =
  ([ChatMembershipsAPI, { chatId, ...params }]) =>
  (dispatch, getState) => {
    const chat = selectChats(getState())?.find((c) => c.id === chatId);
    if (!chat) return;

    // A channel you haven't subscribed to has no server-side membership, so PATCHing it 404s —
    // and the API gateway bounces you out of the chat. Read-tracking only applies once subscribed.
    if (chat.type === 'channel' && !chat.subscribed) return;

    const membership = { ...chat.membership, ...params };
    if (isSameObject(chat.membership, membership)) return;

    ChatMembershipsAPI.update(chatId, membership);
    dispatch(membershipUpdated({ chatId, membership }));
  };

export const subscribe =
  ([ChatMembershipsAPI, { chatId }]) =>
  async (dispatch, getState) => {
    const chat = selectChats(getState())?.find((c) => c.id === chatId);
    if (!chat || chat.subscribed) return;

    // Wait for the server to actually create the membership before flipping `subscribed`. Read-tracking
    // is gated on `subscribed`, so flipping early could race the not-yet-created membership into a 404.
    await ChatMembershipsAPI.subscribe(chatId);
    dispatch(subscriptionChanged({ chatId, subscribed: true }));
  };

export const unsubscribe =
  ([ChatMembershipsAPI, { chatId }]) =>
  (dispatch, getState) => {
    const chat = selectChats(getState())?.find((c) => c.id === chatId);
    if (!chat?.subscribed) return;

    ChatMembershipsAPI.unsubscribe(chatId);
    dispatch(subscriptionChanged({ chatId, subscribed: false }));
  };

export default chatsSlice.reducer;
